import React from "react";
import { Box, Typography, Modal, Button, Alert, Chip } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FolderIcon from "@mui/icons-material/Folder";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useDeleteCategory } from "../../hooks/api/useCategory";
import { Category } from "./types/categoryTypes";

interface DeleteSubcategoryModalProps {
  open: boolean;
  onClose: () => void;
  subcategory: Category;
  parentCategory?: Category;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 4,
  maxWidth: 500,
  minWidth: 300,
  boxShadow: 24,
  textAlign: "center",
  color: "black"
};

const DeleteSubcategoryModal: React.FC<DeleteSubcategoryModalProps> = ({
  open,
  onClose,
  subcategory,
  parentCategory
}) => {
  const deleteCategory = useDeleteCategory();
  const { t } = useKivunovaTranslation();

  const handleDelete = async () => {
    try {
      await deleteCategory.mutateAsync({ id: subcategory.id });
      onClose();
    } catch (err) {
      console.error("❌ Failed to delete subcategory:", err);
    }
  };

  const hasProducts = (subcategory.productCount || 0) > 0;
  const hasChildren = subcategory.children && subcategory.children.length > 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />

        <Typography variant="h6" mb={1} fontSize={18} fontWeight={600}>
          {t("delete-subcategory", "Delete Subcategory")}
        </Typography>

        {/* Subcategory Info */}
        <Box
          sx={{
            backgroundColor: "grey.100",
            borderRadius: 1,
            p: 2,
            mb: 2,
            textAlign: "left"
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <FolderIcon color="primary" fontSize="small" />
            <Typography variant="body2" fontWeight={600}>
              {subcategory.name}
            </Typography>
            {subcategory.depth !== undefined && (
              <Chip
                label={`L${subcategory.depth}`}
                size="small"
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Box>
          {parentCategory && (
            <Typography variant="caption" color="text.secondary">
              {t("parent", "Parent")}: {parentCategory.name}
            </Typography>
          )}
          {subcategory.path && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontFamily: "monospace", mt: 0.5 }}
            >
              {t("path", "Path")}: {subcategory.path}
            </Typography>
          )}
        </Box>

        {/* Warning Messages */}
        {(hasProducts || hasChildren) && (
          <Box sx={{ mb: 2 }}>
            {hasProducts && (
              <Alert severity="error" sx={{ mb: 1, textAlign: "left" }}>
                <Typography variant="body2">
                  <strong>{t("warning", "Warning")}:</strong>{" "}
                  {t(
                    "subcategory-has-products-warning",
                    "This subcategory contains"
                  )}{" "}
                  <strong>
                    {subcategory.productCount}{" "}
                    {t("products-count", "product(s)")}
                  </strong>
                  .{" "}
                  {t(
                    "delete-products-warning",
                    "Deleting it will remove all associated products."
                  )}
                </Typography>
              </Alert>
            )}
            {hasChildren && (
              <Alert severity="error" sx={{ textAlign: "left" }}>
                <Typography variant="body2">
                  <strong>{t("warning", "Warning")}:</strong>{" "}
                  {t(
                    "subcategory-has-children-warning",
                    "This subcategory has"
                  )}{" "}
                  <strong>{t("child-categories", "child categories")}</strong>.{" "}
                  {t(
                    "delete-children-warning",
                    "Deleting it will also delete all nested subcategories and their products."
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" mb={3}>
          {hasProducts || hasChildren
            ? t(
                "delete-subcategory-associated-data-warning",
                "This action cannot be undone and will permanently delete all associated data."
              )
            : t(
                "delete-subcategory-confirm",
                "Are you sure you want to delete this subcategory? This action cannot be undone."
              )}
        </Typography>

        <Box display="flex" justifyContent="center" gap={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              backgroundColor: "white",
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                opacity: 0.8,
                color: "white"
              },
              textTransform: "none",
              minWidth: 100
            }}
          >
            {t("cancel", "Cancel")}
          </Button>

          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            {t("delete", "Delete")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteSubcategoryModal;
